import React, { useContext } from 'react';
import { GetOrganizationLazyQueryHookResult } from '../../../models/graphql';
import { InviteTeamMemberModal } from '../InviteTeamMemberModal';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import { SettingsTeamMember } from '../../../models/settings-pages';
import { UserCard } from '../UserCard';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import classes from './TeamManagement.module.scss';
import { CenteredContentLayout } from '../../../components/containers/CenteredContentLayout';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { AppContext } from 'providers/AppProvider';
import { OnboardingContext } from '../../Onboarding/states/OnboardingProvider';
import { Tooltip } from '../../../components/actions/Tooltip';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { OnboardingStaticContent } from '../../Onboarding/constants/content';

export type TeamManagementViewProps = {
  openInviteModal: boolean;
  setOpenInviteModal: (openInviteModal: boolean) => void;
  sortedUserAssignmentsAndInvites: SettingsTeamMember[] | undefined;
  organizationResults: GetOrganizationLazyQueryHookResult[1];
  getOrganization: GetOrganizationLazyQueryHookResult[0];
  mappedError: string | JSX.Element;
};

export const TeamManagementView = (props: TeamManagementViewProps): JSX.Element => {
  const { canAddUser } = useOrganizationPlan();
  const { setLimitReachedModalState } = useContext(AppContext);
  const { showOnboarding, state } = useContext(OnboardingContext);
  const { version } = state;
  const disableInvite = OnboardingStaticContent[`v${version}`].disableInvite;
  const UserCardComponents = props.sortedUserAssignmentsAndInvites?.map(user => {
    return (
      <UserCard
        key={`UserCard-${user.status}-${user.id}`}
        user={user}
        workspaces={props.organizationResults.data?.organization?.workspaces}
      />
    );
  });
  const handleAddTeamClick = () => {
    if (!canAddUser()) {
      setLimitReachedModalState({ isOpen: true, type: 'users' });
      return;
    }
    props.setOpenInviteModal(true);
  };
  const headerActionButton: JSX.Element = (
    <>
      <div className={'hide-for-small show-for-tablet-portrait'} id={'addTeamMemberButtonDesktop'}>
        <Button
          type={'button'}
          variant={'solid'}
          size={'medium'}
          color={ButtonColorsEnum.secondary}
          icon={{ side: 'left', value: IconsEnum.plus }}
          disabled={showOnboarding && disableInvite}
          action={handleAddTeamClick}>
          Add a Team Member
        </Button>
        {showOnboarding && disableInvite ? (
          <Tooltip
            type={'regular'}
            pointer={true}
            target={`#addTeamMemberButtonDesktop`}
            autoZIndex={false}
            position={'bottom'}
            style={{ zIndex: scssVars['z-index-overmodal'] }}>
            You will be able to add team member later.
          </Tooltip>
        ) : null}
      </div>
      <div className={'show-for-small hide-for-tablet-portrait'} id={'addTeamMemberButtonMobile'}>
        <Button
          type={'button'}
          variant={'solid'}
          size={'medium'}
          disabled={showOnboarding && disableInvite}
          color={ButtonColorsEnum.secondary}
          icon={{ side: 'center', value: IconsEnum.plus }}
          action={handleAddTeamClick}
        />
        {showOnboarding && disableInvite ? (
          <Tooltip
            type={'regular'}
            pointer={true}
            target={`#addTeamMemberButtonMobile`}
            autoZIndex={false}
            position={'bottom'}
            style={{ zIndex: scssVars['z-index-overmodal'] }}>
            You will be able to add team member later.
          </Tooltip>
        ) : null}
      </div>
    </>
  );

  return (
    <CenteredContentLayout
      loading={props.organizationResults.loading}
      headerTitle={'Team Members'}
      hasSidebar
      headerActionButton={!props.mappedError ? headerActionButton : undefined}>
      {props.mappedError ? (
        <div className={'grid-x grid-padding-x align-center'}>
          <div className={'cell small-12'}>
            <div className={classes.BoxMessageError}>
              <BoxNotification type={'error'}>
                Something went wrong. Please refresh the page and try again.
              </BoxNotification>
            </div>
          </div>
        </div>
      ) : (
        <>{UserCardComponents}</>
      )}

      {props.openInviteModal && props.organizationResults.data?.organization && (
        <InviteTeamMemberModal
          onClose={() => props.setOpenInviteModal(false)}
          organization={props.organizationResults.data.organization}
        />
      )}
    </CenteredContentLayout>
  );
};
